(function (global) {
  'use strict';

  if (global.CmxBrandsCarousel) return;

  var POINTER_DRAG_START_THRESHOLD = 6;
  var NATIVE_TOUCH_QUERY = '(hover: none), (pointer: coarse)';
  var REDUCED_MOTION_QUERY = '(prefers-reduced-motion: reduce)';
  var NATIVE_SCROLL_IDLE_DELAY = 350;
  var MAX_AUTOPLAY_FRAME_DELTA = 64;

  function normalizeLoopOffset(offset, loopWidth) {
    if (!Number.isFinite(offset)) return 0;
    if (!Number.isFinite(loopWidth) || loopWidth <= 0) return offset;

    var normalized = offset % loopWidth;
    if (normalized > 0) normalized -= loopWidth;
    if (Math.abs(normalized) < 0.001) return 0;

    return normalized;
  }

  function readTransformX(transform) {
    if (!transform || transform === 'none') return 0;

    if (typeof global.DOMMatrixReadOnly === 'function') {
      try {
        return new global.DOMMatrixReadOnly(transform).m41;
      } catch (error) {
        // Continue with the string parser for test doubles and older browsers.
      }
    }

    var matrix3d = transform.match(/^matrix3d\((.+)\)$/);
    if (matrix3d) {
      var matrix3dValues = matrix3d[1].split(',').map(Number);
      return Number.isFinite(matrix3dValues[12]) ? matrix3dValues[12] : 0;
    }

    var matrix = transform.match(/^matrix\((.+)\)$/);
    if (matrix) {
      var matrixValues = matrix[1].split(',').map(Number);
      return Number.isFinite(matrixValues[4]) ? matrixValues[4] : 0;
    }

    var translate = transform.match(/^translate(?:3d|X)?\(\s*(-?[\d.]+)px/i);
    return translate ? parseFloat(translate[1]) : 0;
  }

  function getLoopWidth(firstOffset, duplicateOffset, fallbackWidth) {
    var measuredWidth = duplicateOffset - firstOffset;
    if (Number.isFinite(measuredWidth) && measuredWidth > 0) return measuredWidth;
    if (Number.isFinite(fallbackWidth) && fallbackWidth > 0) return fallbackWidth / 2;

    return 0;
  }

  function getRecenteredScrollLeft(scrollLeft, loopWidth, tolerance) {
    if (!Number.isFinite(scrollLeft)) return 0;
    if (!Number.isFinite(loopWidth) || loopWidth <= 0) return scrollLeft;

    var edgeTolerance = Number.isFinite(tolerance) ? tolerance : 1;
    if (scrollLeft <= edgeTolerance) return scrollLeft + loopWidth;
    if (scrollLeft >= loopWidth) return scrollLeft - loopWidth;

    return scrollLeft;
  }

  function getAutoplayDistance(loopWidth, durationSeconds, elapsedMilliseconds) {
    if (loopWidth <= 0 || durationSeconds <= 0 || elapsedMilliseconds <= 0) return 0;

    return (loopWidth / durationSeconds) * (elapsedMilliseconds / 1000);
  }

  function requestFrame(callback) {
    if (typeof global.requestAnimationFrame === 'function') {
      return global.requestAnimationFrame(callback);
    }

    return global.setTimeout(callback, 0);
  }

  function cancelFrame(frameId) {
    if (frameId === null) return;

    if (typeof global.cancelAnimationFrame === 'function') {
      global.cancelAnimationFrame(frameId);
    } else {
      global.clearTimeout(frameId);
    }
  }

  class CmxBrandsCarouselElement extends global.HTMLElement {
    constructor() {
      super();

      this.initialized = false;
      this.userPaused = false;
      this.loopWidth = 0;
      this.currentOffset = null;
      this.pointerDrag = null;
      this.suppressClick = false;
      this.suppressClickTimer = null;
      this.resizeObserver = null;
      this.metricsFrame = null;
      this.mobileAutoplayFrame = null;
      this.mobileAutoplayTimestamp = null;
      this.nativeScrollIdleTimer = null;
      this.touchModeQuery = null;
      this.motionQuery = null;

      this.handlePointerDown = this.handlePointerDown.bind(this);
      this.handlePointerMove = this.handlePointerMove.bind(this);
      this.handlePointerUp = this.handlePointerUp.bind(this);
      this.handlePointerCancel = this.handlePointerCancel.bind(this);
      this.handleLostPointerCapture = this.handleLostPointerCapture.bind(this);
      this.handleClickCapture = this.handleClickCapture.bind(this);
      this.handleNativeDragStart = this.handleNativeDragStart.bind(this);
      this.handleNativeScroll = this.handleNativeScroll.bind(this);
      this.handleTouchModeChange = this.handleTouchModeChange.bind(this);
      this.handleMotionPreferenceChange = this.handleMotionPreferenceChange.bind(this);
      this.stepMobileAutoplay = this.stepMobileAutoplay.bind(this);
      this.scheduleLoopMeasurement = this.scheduleLoopMeasurement.bind(this);
    }

    connectedCallback() {
      if (this.initialized) return;

      this.scroller = this.querySelector('[data-carousel-scroller]');
      this.viewport = this.querySelector('[data-carousel-viewport]');
      if (!this.scroller || !this.viewport) return;

      this.brandCount = Math.max(0, parseInt(this.viewport.dataset.carouselBrandCount, 10) || 0);
      this.scrollDuration = Math.max(1, parseFloat(this.dataset.scrollSpeed) || 30);
      this.touchModeQuery = global.matchMedia?.(NATIVE_TOUCH_QUERY) || { matches: false };
      this.motionQuery = global.matchMedia?.(REDUCED_MOTION_QUERY) || { matches: false };
      this.initialized = true;

      this.viewport.addEventListener('pointerdown', this.handlePointerDown);
      this.viewport.addEventListener('pointermove', this.handlePointerMove, { passive: false });
      this.viewport.addEventListener('pointerup', this.handlePointerUp);
      this.viewport.addEventListener('pointercancel', this.handlePointerCancel);
      this.viewport.addEventListener('lostpointercapture', this.handleLostPointerCapture);
      this.viewport.addEventListener('click', this.handleClickCapture, true);
      this.viewport.addEventListener('dragstart', this.handleNativeDragStart);
      this.scroller.addEventListener('scroll', this.handleNativeScroll, { passive: true });

      this.addMediaQueryListener(this.touchModeQuery, this.handleTouchModeChange);
      this.addMediaQueryListener(this.motionQuery, this.handleMotionPreferenceChange);

      this.updateLoopMetrics();
      this.scheduleLoopMeasurement();

      if (typeof global.ResizeObserver === 'function') {
        this.resizeObserver = new global.ResizeObserver(this.scheduleLoopMeasurement);
        this.resizeObserver.observe(this);
      } else {
        global.addEventListener?.('resize', this.scheduleLoopMeasurement);
      }
    }

    disconnectedCallback() {
      if (!this.initialized) return;

      if (this.suppressClickTimer !== null) global.clearTimeout(this.suppressClickTimer);
      if (this.nativeScrollIdleTimer !== null) global.clearTimeout(this.nativeScrollIdleTimer);
      cancelFrame(this.metricsFrame);
      this.stopMobileAutoplay();
      this.resizeObserver?.disconnect();
      global.removeEventListener?.('resize', this.scheduleLoopMeasurement);

      this.viewport?.removeEventListener('pointerdown', this.handlePointerDown);
      this.viewport?.removeEventListener('pointermove', this.handlePointerMove);
      this.viewport?.removeEventListener('pointerup', this.handlePointerUp);
      this.viewport?.removeEventListener('pointercancel', this.handlePointerCancel);
      this.viewport?.removeEventListener('lostpointercapture', this.handleLostPointerCapture);
      this.viewport?.removeEventListener('click', this.handleClickCapture, true);
      this.viewport?.removeEventListener('dragstart', this.handleNativeDragStart);
      this.scroller?.removeEventListener('scroll', this.handleNativeScroll);

      this.removeMediaQueryListener(this.touchModeQuery, this.handleTouchModeChange);
      this.removeMediaQueryListener(this.motionQuery, this.handleMotionPreferenceChange);

      this.pointerDrag = null;
      this.initialized = false;
    }

    scheduleLoopMeasurement() {
      if (this.metricsFrame !== null) return;

      this.metricsFrame = requestFrame(() => {
        this.metricsFrame = null;
        this.updateLoopMetrics();
      });
    }

    updateLoopMetrics() {
      if (!this.viewport || this.brandCount <= 0) return;

      var firstItem = this.viewport.children[0];
      var duplicateStart = this.viewport.children[this.brandCount];
      var firstOffset = firstItem?.offsetLeft || 0;
      var duplicateOffset = duplicateStart?.offsetLeft || 0;
      var loopWidth = getLoopWidth(firstOffset, duplicateOffset, this.viewport.scrollWidth);
      if (loopWidth <= 0) return;

      this.loopWidth = loopWidth;
      this.viewport.style.setProperty('--brands-loop-offset', -loopWidth + 'px');

      if (this.userPaused && !this.isNativeTouchMode()) {
        this.setOffset(this.getCurrentOffset());
      }

      this.syncMobileAutoplay();
    }

    addMediaQueryListener(query, listener) {
      if (typeof query?.addEventListener === 'function') {
        query.addEventListener('change', listener);
      } else if (typeof query?.addListener === 'function') {
        query.addListener(listener);
      }
    }

    removeMediaQueryListener(query, listener) {
      if (typeof query?.removeEventListener === 'function') {
        query.removeEventListener('change', listener);
      } else if (typeof query?.removeListener === 'function') {
        query.removeListener(listener);
      }
    }

    isNativeTouchMode() {
      return Boolean(this.touchModeQuery?.matches);
    }

    shouldRunMobileAutoplay() {
      return Boolean(
        this.initialized &&
        this.isNativeTouchMode() &&
        !this.userPaused &&
        !this.motionQuery?.matches &&
        this.loopWidth > 0
      );
    }

    syncMobileAutoplay() {
      if (!this.shouldRunMobileAutoplay()) {
        this.stopMobileAutoplay();
        return;
      }

      if (this.mobileAutoplayFrame !== null) return;

      this.mobileAutoplayTimestamp = null;
      this.mobileAutoplayFrame = requestFrame(this.stepMobileAutoplay);
    }

    stopMobileAutoplay() {
      cancelFrame(this.mobileAutoplayFrame);
      this.mobileAutoplayFrame = null;
      this.mobileAutoplayTimestamp = null;
    }

    stepMobileAutoplay(timestamp) {
      this.mobileAutoplayFrame = null;
      if (!this.shouldRunMobileAutoplay()) {
        this.mobileAutoplayTimestamp = null;
        return;
      }

      var currentTimestamp = Number.isFinite(timestamp) ? timestamp : Date.now();
      if (this.mobileAutoplayTimestamp !== null) {
        var elapsed = Math.min(
          Math.max(0, currentTimestamp - this.mobileAutoplayTimestamp),
          MAX_AUTOPLAY_FRAME_DELTA
        );
        this.scroller.scrollLeft += getAutoplayDistance(
          this.loopWidth,
          this.scrollDuration,
          elapsed
        );

        if (this.scroller.scrollLeft >= this.loopWidth) {
          this.scroller.scrollLeft -= this.loopWidth;
        }
      }

      this.mobileAutoplayTimestamp = currentTimestamp;
      this.mobileAutoplayFrame = requestFrame(this.stepMobileAutoplay);
    }

    pauseMobileAutoplayByUser() {
      this.userPaused = true;
      this.stopMobileAutoplay();
    }

    handleNativeScroll() {
      if (!this.isNativeTouchMode() || !this.userPaused || this.loopWidth <= 0) return;

      if (this.nativeScrollIdleTimer !== null) global.clearTimeout(this.nativeScrollIdleTimer);
      this.nativeScrollIdleTimer = global.setTimeout(() => {
        this.nativeScrollIdleTimer = null;
        var recentered = getRecenteredScrollLeft(this.scroller.scrollLeft, this.loopWidth, 1);
        if (Math.abs(recentered - this.scroller.scrollLeft) > 0.5) {
          this.scroller.scrollLeft = recentered;
        }
      }, NATIVE_SCROLL_IDLE_DELAY);
    }

    handleTouchModeChange() {
      this.stopMobileAutoplay();
      if (!this.isNativeTouchMode()) {
        this.scroller.scrollLeft = 0;
      }
      this.syncMobileAutoplay();
    }

    handleMotionPreferenceChange() {
      this.syncMobileAutoplay();
    }

    getCurrentOffset() {
      if (this.userPaused && Number.isFinite(this.currentOffset)) return this.currentOffset;

      var computedTransform = global.getComputedStyle(this.viewport).transform;
      return readTransformX(computedTransform);
    }

    setOffset(offset) {
      var normalizedOffset = normalizeLoopOffset(offset, this.loopWidth);
      this.currentOffset = normalizedOffset;
      this.viewport.style.transform = 'translate3d(' + normalizedOffset + 'px, 0, 0)';
      return normalizedOffset;
    }

    pauseAtOffset(offset) {
      this.userPaused = true;
      this.viewport.style.animation = 'none';
      return this.setOffset(offset);
    }

    handlePointerDown(event) {
      if (event.pointerType === 'touch') {
        if (this.isNativeTouchMode()) this.pauseMobileAutoplayByUser();
        return;
      }

      if (this.isNativeTouchMode()) return;
      if (event.button !== 0 || event.isPrimary === false) return;

      this.pointerDrag = {
        pointerId: event.pointerId,
        pointerType: event.pointerType,
        startX: event.clientX,
        startY: event.clientY,
        currentX: event.clientX,
        startOffset: this.getCurrentOffset(),
        dragging: false,
      };
    }

    handlePointerMove(event) {
      var drag = this.pointerDrag;
      if (!drag || drag.pointerId !== event.pointerId) return;

      drag.currentX = event.clientX;
      var deltaX = event.clientX - drag.startX;
      var deltaY = event.clientY - drag.startY;

      if (!drag.dragging) {
        if (
          Math.abs(deltaX) <= POINTER_DRAG_START_THRESHOLD ||
          Math.abs(deltaX) <= Math.abs(deltaY)
        ) {
          return;
        }

        drag.dragging = true;
        this.viewport.setPointerCapture?.(drag.pointerId);
        drag.startOffset = this.pauseAtOffset(drag.startOffset);
        this.setAttribute('data-brands-dragging', '');
      }

      event.preventDefault();
      this.setOffset(drag.startOffset + deltaX);
    }

    handlePointerUp(event) {
      this.finishPointerDrag(event, false);
    }

    handlePointerCancel(event) {
      this.finishPointerDrag(event, true);
    }

    handleLostPointerCapture(event) {
      this.finishPointerDrag(event, true);
    }

    finishPointerDrag(event, cancelled) {
      var drag = this.pointerDrag;
      if (!drag || drag.pointerId !== event.pointerId) return;

      this.pointerDrag = null;
      if (this.viewport.hasPointerCapture?.(drag.pointerId)) {
        this.viewport.releasePointerCapture(drag.pointerId);
      }

      if (!drag.dragging) return;

      event.preventDefault?.();
      this.removeAttribute('data-brands-dragging');
      if (!cancelled) this.armClickSuppression(0);
    }

    armClickSuppression(delay) {
      this.suppressClick = true;
      if (this.suppressClickTimer !== null) global.clearTimeout(this.suppressClickTimer);
      this.suppressClickTimer = global.setTimeout(() => {
        this.suppressClick = false;
        this.suppressClickTimer = null;
      }, delay || 0);
    }

    handleClickCapture(event) {
      if (!this.suppressClick) return;

      event.preventDefault();
      event.stopImmediatePropagation?.();
      event.stopPropagation?.();
      this.suppressClick = false;
      if (this.suppressClickTimer !== null) {
        global.clearTimeout(this.suppressClickTimer);
        this.suppressClickTimer = null;
      }
    }

    handleNativeDragStart(event) {
      event.preventDefault();
    }
  }

  global.CmxBrandsCarousel = {
    Element: CmxBrandsCarouselElement,
    helpers: {
      getAutoplayDistance: getAutoplayDistance,
      getLoopWidth: getLoopWidth,
      getRecenteredScrollLeft: getRecenteredScrollLeft,
      normalizeLoopOffset: normalizeLoopOffset,
      readTransformX: readTransformX,
    },
  };

  if (!global.customElements.get('cmx-brands-carousel')) {
    global.customElements.define('cmx-brands-carousel', CmxBrandsCarouselElement);
  }
})(window);
